import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { Feature } from 'geojson';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ControlPosition, useControl } from 'react-map-gl';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  MapDrawIDEnum,
  selectClearMapDrawFeatures,
  selectCurrMapDrawID,
  setCurrMapDrawFeatures,
} from '../onboarding/onboardingSlice';
import { selectCurrBlockID } from '../select/selectSlice';

export interface FeaturesById {
  [id: string]: Feature;
}

type Props = ConstructorParameters<typeof MapboxDraw>[0] & {
  position?: ControlPosition;
};

const DrawControl: React.FC<Props> = (props) => {
  const dispatch = useAppDispatch();
  const currBlockID = useAppSelector(selectCurrBlockID);
  const currMapDrawID = useAppSelector(selectCurrMapDrawID);
  const clearMapDrawFeatures = useAppSelector(selectClearMapDrawFeatures);
  const currMapDrawIDRef = useRef<MapDrawIDEnum | null>(null);
  const [features, setFeatures] = useState<FeaturesById>({});

  // The map update callbacks are only getting called once, so we need to use a ref to keep track of the current map draw id so that it has the current value when the callbacks are called instead of the value when the callbacks were created.
  useEffect(() => {
    if (currMapDrawIDRef.current !== currMapDrawID) {
      currMapDrawIDRef.current = currMapDrawID;
    }
  }, [currMapDrawIDRef, currMapDrawID]);

  // Update the store with the current features whenever the features state changes.
  useEffect(() => {
    dispatch(setCurrMapDrawFeatures(features));
  }, [dispatch, features]);

  // Reset the features both locally and in the store when the current block id changes or the reset draw controls button is clicked.
  useEffect(() => {
    setFeatures({});
    dispatch(setCurrMapDrawFeatures({}));
  }, [dispatch, currBlockID, clearMapDrawFeatures]);

  if (Object.keys(features).length > 0) console.log('features: ', features);

  const onCreate = useCallback(
    (e: { features: Feature[] }) => {
      console.log('e: ', e);

      setFeatures((currFeatures) => {
        const newFeatures = { ...currFeatures };
        for (const feature of e.features) {
          if (
            feature.id !== undefined &&
            typeof currMapDrawIDRef.current === 'string'
          ) {
            const newFeature = { ...feature, id: currMapDrawIDRef.current };
            newFeatures[currMapDrawIDRef.current] = newFeature;
          }
        }
        return newFeatures;
      });
    },
    [currMapDrawIDRef]
  );

  const onUpdate = useCallback(
    (e: { features: Feature[] }) => {
      console.log('e: ', e);

      setFeatures((currFeatures) => {
        const newFeatures = { ...currFeatures };
        for (const feature of e.features) {
          if (feature.id !== undefined) {
            newFeatures[feature.id] = feature;
          }
        }
        return newFeatures;
      });
    },
    [currMapDrawID]
  );

  const onDelete = useCallback(
    (e: { features: Feature[] }) => {
      setFeatures((currFeatures) => {
        const newFeatures = { ...currFeatures };
        for (const feature of e.features) {
          if (feature.id !== undefined) {
            delete newFeatures[feature.id];
          }
        }
        return newFeatures;
      });
    },
    [currMapDrawID]
  );

  useControl<MapboxDraw>(
    () => new MapboxDraw(props),
    ({ map }) => {
      map.on('draw.create', onCreate);
      // map.on('draw.create', (e: { features: Feature[] }) => onCreate(e));
      map.on('draw.update', onUpdate);
      map.on('draw.delete', onDelete);
    },
    ({ map }) => {
      map.off('draw.create', onCreate);
      map.off('draw.update', onUpdate);
      map.off('draw.delete', onDelete);
    },
    {
      position: 'top-left',
    }
  );

  return null;
};

export default DrawControl;
